<script setup>
// =============== 1. 초기 설정 ===============
// 외부 라이브러리
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';

// Composables
import { useAnalysisServices } from '@/composables/saju-insight/useAnalysisServices';
import { useContentRate } from '@/composables/saju-insight/useContentRate';
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import { useMonthlyFortuneFreeData } from '@/composables/saju-insight/useMonthlyFortuneFreeData';
import { useRnBridge } from '@/composables/useRnBridge';
import {
  MONTHLY_FORTUNE_CARD_TYPE_ID,
  MONTHLY_FORTUNE_PARENT_TYPE_ID,
} from '@/components/saju-insight/monthly-fortune/tocCards';
import { isObjectEmpty } from '@/composables/functions';

// API
import { addContentLogAPI } from '@/api/logServiceApi';

// Store
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';

// Components
import MonthlyFortuneCover from '@/pages/mobile/analysis-saju-monthly-fortune/_summary_01.vue';
import MonthlyFortuneTOC from '@/pages/mobile/analysis-saju-monthly-fortune/_summary_02.vue';
import MonthlyFortuneMobileLetter from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileLetter.vue';
import MonthlyFortuneMobileEnergy from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileEnergy.vue';
import MonthlyFortuneMobileLucky from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileLucky.vue';
import MonthlyFortuneMobileRomance from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileRomance.vue';
import MonthlyFortuneMobileHealth from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileHealth.vue';
import MonthlyFortuneMobileRomanceStyle from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileRomanceStyle.vue';
import MonthlyFortuneMobileMoney from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileMoney.vue';
import MonthlyFortuneMobileCapability from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileCapability.vue';
import MonthlyFortuneMobileCaution from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileCaution.vue';
import MonthlyFortuneMobileMode from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileMode.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import PopMonthlyFortunePurchase from '@/components/saju-insight/monthly-fortune/PopMonthlyFortunePurchase.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import AlertComponent from '@/components/common/Alert.vue';

provide('fullWidthMode', true);

definePage({ meta: { layout: 'empty' } });

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const { serviceData, serviceContents, fetchContentByKey } = useAnalysisServices();
const mobileSajuLnbStore = useMobileSajuLnbStore();

const { sajuData } = storeToRefs(analysisDataStore);

// =============== 2. Alert / Dialog ===============
const { alertRef, dialogTitle, dialogText, dialogNextUrl, showAlert } = useDialogManager();

// =============== 3. 웹뷰 통신 ===============
const guestUserId = ref(null);
const isInitializing = ref(true);

const { debuggingHandler } = useRnBridge();

// =============== 4. PC 환경 ===============
const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];

// =============== 5. 사주 데이터 ===============
const { initWithValidation, isLoadingSajuData } = useSajuData();

const closeSajuInsight = () => {
  router.push('/saju-service');
};

const initializeSajuData = () =>
  initWithValidation(guestUserId.value, { showAlert, onRestricted: closeSajuInsight });

// =============== 6. 데이터 조회 ===============

// =============== 6-1. 이용중인 분석 정보 ===============

/** 이용중인 낭만월운 정보 요청 중 상태 */
const isLoadingAnalysisInfo = ref(false);

/** 이용중인 낭만월운 정보 요청 완료 여부 */
const hasLoadedAnalysisInfo = ref(false);

/** 이용중인 낭만월운 정보 요청 */
const getAnalysisInfo = async () => {
  isLoadingAnalysisInfo.value = true;
  hasLoadedAnalysisInfo.value = false;

  try {
    await fetchContentByKey('monthlyFortune');
    hasLoadedAnalysisInfo.value = true;
  } catch (e) {
    debuggingHandler({
      action: 'error',
      message: `낭만월운 > 1. 이용중인 낭만월운 정보 요청 실패: ${e}`,
    });
  } finally {
    isLoadingAnalysisInfo.value = false;
  }
};

// =============== 6-2. 페이지 데이터 새로고침 ===============
/** 낭만월운 페이지 데이터 로딩 상태 */
const isRefreshingPageData = ref(false);

/** 낭만월운 페이지 데이터 새로고침 */
const refreshMonthlyFortunePageData = async () => {
  isRefreshingPageData.value = true;
  try {
    await Promise.all([
      getAnalysisInfo(),
      // 권한·만료일은 access(hasMonthlyFortuneAccess)·구매이력으로 충당. 단순 log(10)은 미사용 computed만
      // 채워 무용이라 제거. 자식 typeId(42~46) 권한 판정을 위해 구매이력 전체 캐시.
      analysisStore.fetchMonthlyFortunePurchaseHistory(guestUserId.value),
      paymentsStore.fetchTicketCount(),
      paymentsStore.getOwnPoint(),
    ]);
    // 권한 캐시 확정 후 낭만월운 데이터 조회 (구매여부에 따라 flag 자동 전환)
    fetchMonthlyFortunePaidNow();
  } catch (e) {
    debuggingHandler({ action: 'error', message: `낭만월운 > 페이지 새로고침 실패: ${e}` });
  } finally {
    isRefreshingPageData.value = false;
  }
};

// =============== 6-7. 낭만월운 데이터 (구매여부에 따라 flag 자동 전환) ===============
/**
 * 표지/목차에서 선택한 월 (store 공유).
 * _summary_01/_summary_02 의 월 선택 드롭다운과 같은 store 값을 바라보므로,
 * 사용자가 다른 월로 바꾸면 자동으로 해당 월 데이터를 fetch 한다.
 */
const paidYear = computed(() => analysisStore.monthlyFortuneSelectedYear);
const paidMonth = computed(() => analysisStore.monthlyFortuneSelectedMonth);

/**
 * (year, month) 에서 실제 구매된 typeId 목록.
 * base-service 는 analysisTypeId=10 고정 호출이며, 구매한 카테고리 번호를
 * suggestionFlag 콤마 문자열로 전달한다.
 */
const resolvePurchasedMonthlyFortuneTypeIds = (year, month) => {
  if (!guestUserId.value) return [];
  const tids = [
    MONTHLY_FORTUNE_PARENT_TYPE_ID,
    ...Object.values(MONTHLY_FORTUNE_CARD_TYPE_ID),
  ];
  return tids.filter((tid) =>
    analysisStore.hasMonthlyFortuneAccess(guestUserId.value, year, month, tid),
  );
};

const purchasedMonthlyFortuneTypeIds = computed(() =>
  resolvePurchasedMonthlyFortuneTypeIds(paidYear.value, paidMonth.value),
);

const {
  freeData: monthlyFortunePaidData,
  ensureFreeData: ensureMonthlyFortuneData,
  invalidateMonth: invalidateMonthlyFortuneMonth,
} = useMonthlyFortuneFreeData(
  guestUserId,
  paidYear,
  paidMonth,
  resolvePurchasedMonthlyFortuneTypeIds,
);

/** 낭만월운 본문 데이터 fetch 진행 중 플래그 — 응답 도착 전 로딩 유지 */
const isFetchingMonthlyFortunePaid = ref(false);
const fetchMonthlyFortunePaidNow = async () => {
  isFetchingMonthlyFortunePaid.value = true;
  try {
    await ensureMonthlyFortuneData();
  } finally {
    isFetchingMonthlyFortunePaid.value = false;
  }
};

/** 구매한 카테고리가 늘어나면(결제 직후) 캐시 무효화 후 새 suggestionFlag 로 재요청 */
watch(
  purchasedMonthlyFortuneTypeIds,
  (newVal, oldVal) => {
    if ((newVal?.length || 0) > (oldVal?.length || 0)) {
      invalidateMonthlyFortuneMonth(paidYear.value, paidMonth.value);
      fetchMonthlyFortunePaidNow();
    }
  },
  { deep: true },
);

/** 선택월 변경 시 해당 월 데이터 자동 fetch */
watch([paidYear, paidMonth], () => {
  fetchMonthlyFortunePaidNow();
});

// =============== 7. 컨텐츠 평가 ===============
const { ratingResult, ratingStatus, getRate, postRate } = useContentRate(
  'MONTHLY_FORTUNE',
  guestUserId,
);

// =============== 8. 페이지 상태 ===============
const showSummary02 = ref(false);

const showPopAnalysisInfo = ref(false);
const showPopAnalysisInfoOther = ref(false);

// ticket-detail 에서 ?openPurchase=1 로 진입한 경우 월 선택 구매 팝업 자동 노출
const showPurchasePopup = ref(false);
const clearOpenPurchaseQuery = () => {
  if (!route.query.openPurchase) return;
  const newQuery = { ...route.query };
  delete newQuery.openPurchase;
  router.replace({ path: route.path, query: newQuery });
};
const handlePurchasePopupSuccess = () => {
  clearOpenPurchaseQuery();
  paymentSuccessDo(true);
  showSummary02.value = true;
  nextTick(() => window.scrollTo(0, 0));
};
const handlePurchasePopupClose = () => {
  showPurchasePopup.value = false;
  clearOpenPurchaseQuery();
};

// =============== 9. 팝업 / 모달 ===============
const showPopAnalysisInfoDo = async () => {
  if (isRefreshingPageData.value) return;

  // 낭만월운권 사용중일 때,
  if (serviceData.monthlyFortune.permission.value) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  if (
    paymentsStore?.points < serviceContents.monthlyFortune.value?.pointPrice &&
    serviceData.monthlyFortune.ticketCount.value === 0
  ) {
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
};

// 하단 탭 영역
const showPopDesc = ref(false);
const openPopDesc = () => {
  if (isRefreshingPageData.value) return;
  if (!serviceData.monthlyFortune.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  showPopDesc.value = true;
};
const closePopDesc = () => {
  showPopDesc.value = false;
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  router.push({ path: '/purchase/points', query: { next: route.fullPath, shortageAmount } });
  showPopAnalysisInfo.value = false;
};

const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};

const handleChargePopOtherProduct = () => {
  router.push('/purchase/tickets');
  showPopAnalysisInfoOther.value = false;
};

/** 낭만월운 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  if (success) {
    try {
      await refreshMonthlyFortunePageData();
      await getRate();
      mobileSajuLnbStore.setNeedRefresh(true);
    } catch (e) {
      debuggingHandler({ action: 'error', message: `낭만월운권 결제 후 새로고침 중 오류: ${e}` });
    }
  }
};

// =============== 12. 공통 액션 ===============
/** 팝업 닫기 — 카드 콘텐츠 진입 상태면 목차로 복귀, 아니면 상위 페이지로 */
const close = () => {
  if (selectedCard.value) {
    logCardContentClose();
    selectedCard.value = null;
    nextTick(() => window.scrollTo(0, 0));
    return;
  }
  router.push(`/m/saju-insight/${guestUserId.value}`);
};

const resetPopup = () => {
  showSummary02.value = false;
  showPopDesc.value = false;
};

const onClickRecharge = (shortageAmount) => {
  router.push({
    path: '/purchase/points',
    query: { next: route.fullPath, shortageAmount },
  });
};

const handelShowSummary02Open = () => {
  showSummary02.value = true;
  window.scrollTo(0, 0);
};

/** 목차 카드 선택 → 카드별 모바일 풀페이지 콘텐츠로 진입 */
const selectedCard = ref(null);
const handleSelectCard = (card) => {
  selectedCard.value = card;
  nextTick(() => window.scrollTo(0, 0));
};
/** 카드 콘텐츠 닫기 로그 — PC 드로어 닫기와 동일 content_type 재사용(모바일은 풀페이지 복귀 시점) */
const logCardContentClose = () => {
  addContentLogAPI({
    contentType: selectedCard.value?.isPaid
      ? 'am_monthly_drawer_close_paid_opt3'
      : 'am_monthly_drawer_close_free_opt3',
    option3: selectedCard.value?.key || '',
  });
};

const handleBackFromCardContent = () => {
  logCardContentClose();
  selectedCard.value = null;
  nextTick(() => window.scrollTo(0, 0));
};
const handleGoPurchaseHistory = () => {
  // TODO: 구매한 내역 페이지 라우팅
};

// =============== 13. Watch ===============
const scrollPopupToTop = () => {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  document.querySelectorAll('.pop-saju-reading-container.mobile, .pop-ana-saju-reading-inner')
    .forEach((el) => {
      el.scrollTop = 0;
    });
};

watch(showSummary02, () => {
  nextTick(scrollPopupToTop);
});

watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (newId) guestUserId.value = newId;
  },
);

watch(guestUserId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    const result = await initializeSajuData();
    if (result?.failed || result?.unknownTime || result?.futureSaju) return;
    await refreshMonthlyFortunePageData();
    await getRate();
  }
});

// =============== 14. 라이프사이클 ===============
onMounted(async () => {
  // 페이지 재진입 시 항상 현재월로 초기화 (이전 세션에서 선택했던 월이 store 에 남아있음)
  const today = new Date();
  analysisStore.setMonthlyFortuneSelectedMonth(today.getFullYear(), today.getMonth() + 1);

  // 웹 환경: 비로그인 접근 차단
  if (!userId.value && !sessionStorage.getItem('X-Token')) {
    showAlert({ title: '로그인 후 이용 가능합니다.', nextUrl: () => router.push('/auth/login') });
    return;
  }

  // 앱/웹 공통: store에서 guestUserId 읽기
  // store에 없으면 query에서 읽기 (웹 다이렉트 접근 대응)
  // ticket-detail 등에서 query.id 로 명시적으로 새 사주를 지정한 경우 query 가 우선
  const queryId = Number(route.query.id) || null;
  const newId = queryId || mobileSajuLnbStore.guestUserId || null;
  if (newId && (queryId || !mobileSajuLnbStore.guestUserId)) {
    mobileSajuLnbStore.setGuestUserId(newId);
  }

  // 초기화 완료 설정 (웹: 여기서 처리 / 앱 직접 접근: onInit에서 처리 / 웹뷰 내 라우팅: 여기서 처리)
  isInitializing.value = false;

  // 같은 ID여도 강제 재조회 (브라우저 캐시 대응)
  if (newId) {
    guestUserId.value = null;
    await nextTick();
    guestUserId.value = newId;
  }

  // ticket-detail → 낭만월운 이동 시 구매 팝업 자동 노출
  if (route.query.openPurchase === '1') {
    showPurchasePopup.value = true;
  }
});

onUnmounted(() => {
  analysisStore.setMonthlyFortuneIndex(1);
  analysisStore.setSelectedContentIndex(null);
});
</script>

<template>
  <!-- 분석권 및 포인트 부족 -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="serviceContents.monthlyFortune.value"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <!-- 분석권 또는 포인트 사용 가능 -->
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="serviceContents.monthlyFortune.value"
    :ticketCountData="serviceData.monthlyFortune.ticketData.value"
    :ticketCount="serviceData.monthlyFortune.ticketCount.value"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <div class="pop-saju-reading-container mobile st-analysis-monthly-fortune">
    <AnalysisMobileHeader title="낭만월운" :guestUserId="guestUserId" @close="close" />
    <div class="pop-ana-saju-reading-inner intro">
      <MonthlyFortuneCover
        v-if="!showSummary02"
        :guestUserId="guestUserId"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :getAnalysisLogDataList="serviceData.monthlyFortune.logData.value"
        :hasPermission="serviceData.monthlyFortune.permission.value"
        :ticketCountData="serviceData.monthlyFortune.ticketData.value"
        :monthlyFortuneLogData="serviceData.monthlyFortune.logData.value"
        @handelShowSummary02Open="handelShowSummary02Open"
        @recharge="onClickRecharge"
      />
      <AnalysisLoadingOverlay
        :loading="
          showSummary02 &&
          (isInitializing ||
            isLoadingSajuData ||
            isRefreshingPageData ||
            isFetchingMonthlyFortunePaid ||
            !monthlyFortunePaidData)
        "
        :message="'낭만월운 분석중입니다!'"
      />
      <div
        v-if="showSummary02 && !isRefreshingPageData"
        v-show="!selectedCard"
        class="mf-toc-wrap"
      >
        <MonthlyFortuneTOC
          :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
          :ticketCount="paymentsStore?.analysisTicketCount || 0"
          :hasPermission="serviceData.monthlyFortune.permission.value"
          :initializing="isInitializing || isLoadingSajuData || isRefreshingPageData"
          @selectCard="handleSelectCard"
          @updateShowSummary02="(value) => (showSummary02 = value)"
          @goPopAnalysis="showPopAnalysisInfoDo"
          @recharge="onClickRecharge"
          @goPurchaseHistory="handleGoPurchaseHistory"
          @payment-success="paymentSuccessDo"
        />
      </div>

      <!-- 카드별 모바일 콘텐츠 (paidData 도착 전엔 위 오버레이가 노출) -->
      <div
        v-if="
          showSummary02 &&
          selectedCard &&
          monthlyFortunePaidData &&
          !isFetchingMonthlyFortunePaid
        "
        class="mf-card-content"
      >
        <MonthlyFortuneMobileLetter
          v-if="selectedCard.key === 'letter'"
          :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
          :selectedMonth="paidMonth"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileEnergy
          v-else-if="selectedCard.key === 'energy'"
          :selectedMonth="paidMonth"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileLucky
          v-else-if="selectedCard.key === 'lucky'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileRomance
          v-else-if="selectedCard.key === 'romance'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileHealth
          v-else-if="selectedCard.key === 'health'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileRomanceStyle
          v-else-if="selectedCard.key === 'romanceStyle'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileMoney
          v-else-if="selectedCard.key === 'money'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileCapability
          v-else-if="selectedCard.key === 'capability'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileCaution
          v-else-if="selectedCard.key === 'caution'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileMode
          v-else-if="selectedCard.key === 'mode'"
          :selectedMonth="paidMonth"
          :paidData="monthlyFortunePaidData"
        />
        <div v-else class="mf-card-content__placeholder">
          <p>{{ selectedCard.emoji }} {{ selectedCard.title }}</p>
          <p class="hint">선택된 카드: {{ selectedCard.key }} — 콘텐츠 디자인 적용 예정</p>
        </div>
      </div>
    </div>

  </div>

  <!-- TODO: 낭만월운에 맞는 팝업 추가 필요 -->

  <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" :nextUrl="dialogNextUrl" />

  <!-- ticket-detail 에서 진입 시 자동 노출되는 월 선택 구매 팝업 -->
  <PopMonthlyFortunePurchase
    :visible="showPurchasePopup"
    :guestUserId="guestUserId"
    @close="handlePurchasePopupClose"
    @payment-success="handlePurchasePopupSuccess"
  />
</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common-page.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

/* 분석 로딩 오버레이를 viewport 기준 고정 — drawer 등으로 부모 높이가 커져도
   오버레이가 화면 중앙에 표시되도록 한다 */
.pop-saju-reading-container.mobile.st-analysis-monthly-fortune
  .analysis__loading-overlay {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* 네이버 인앱브라우저 주소창 오버레이 방지: 문서 레벨 스크롤 전환 */
.pop-saju-reading-container.mobile.st-analysis-monthly-fortune {
  height: auto;
  min-height: 100vh;
  overflow: visible;

  /* AnalysisMobileHeader가 자체적으로 safe-area-top을 포함하므로
     상위 레이아웃(empty.vue main)의 padding-top: safe-area-top과 이중 적용 방지 */
  margin-top: calc(-1 * var(--safe-area-top, 0px));

  > .header {
    position: sticky;
    top: 0;
    z-index: 20;
  }
}

.pop-ana-saju-reading-inner {
  height: auto;
  min-height: calc(100vh - 52px);
  overflow: visible;
  &.intro {
    overflow: visible;
    padding-bottom: var(--safe-area-bottom, 0px);
  }
  &.st-page {
    height: auto;
    min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
    overflow: visible;
    padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  }
  .btn-footer-box {
    position: fixed;
    bottom: var(--safe-area-bottom, 0px);
    left: 0;
    right: 0;
    padding: 16px;
    .btn-white {
      display: flex;
      width: 100%;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      color: $analysis-personality;
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }
  }
  .pop-body {
    height: auto;
    padding: 0px;
    flex: 1;
    overflow: visible;
    .v-row {
      margin: 0px !important;
    }
    .personality-wrapper {
      padding: 24px 16px;
    }
    &.rating {
      height: 100%;
    }
  }
}
.st-analysis-monthly-fortune {
  .pop-ana-saju-reading-inner,
  .overview-cover {
    background: #f9f5eb;
  }
}
.st-analysis-monthly-fortune .pop-ana-saju-reading-inner .pop-body {
  background: #fff;
}

/* 카드 콘텐츠 표시 중에는 카드 배경(#f2f8ef)이 safe-area-bottom 영역까지 이어지도록
   .intro 및 컨테이너 배경을 카드 배경과 맞춤 (iPhone에서 흰 여백이 노출되는 문제 해결) */
.st-analysis-monthly-fortune:has(.mf-card-content) {
  background: #f2f8ef;
}
.st-analysis-monthly-fortune .pop-ana-saju-reading-inner.intro:has(.mf-card-content) {
  background: #f2f8ef;
}

.st-analysis-monthly-fortune .pop-ana-saju-reading-inner.st-content-page {
  background-color: #fff !important;
  padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  overflow-x: hidden !important;

  .contentsListBtnBox.mobile {
    position: fixed;
    bottom: calc(52px + var(--safe-area-bottom, 0px));
    right: 0;
  }
}
</style>
